import {Component, OnInit, inject} from '@angular/core';
import {Sort} from '@angular/material/sort';
import {PageEvent} from '@angular/material/paginator';
import {ModalService} from '../../../shared/service/modal/modal.service';
import {CommissionRulesService} from '../../service/commission-rules.service';
import {CommissionRuleItem, CommissionEvaluationItem} from '../../../shared/model/commission-rules-result';
import {CommissionRuleModalComponent} from '../commission-rule-modal/commission-rule-modal.component';
import {CommissionRuleDeleteDialogComponent} from '../commission-rule-delete-dialog/commission-rule-delete-dialog.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AzureAuthService} from 'src/infrastructure/services/azure-auth.service';

@Component({
    selector: 'commission-rules-grid',
    templateUrl: './commission-rules-grid.component.html',
    standalone: false,
    styles: [`
        .evaluation-filters {
            padding: 16px 0 0 16px;
            display: flex;
            gap: 16px;
            align-items: center;
        }
        .status-filter {
            width: 180px;
        }
        .date-filter {
            width: 180px;
        }
        .cell-value-row {
            font-size: 12px;
            white-space: normal;
            word-break: break-word;
            line-height: 1.4;
        }
        :host ::ng-deep .wrap-cell {
            white-space: normal !important;
            overflow: visible !important;
            padding-top: 8px;
            padding-bottom: 8px;
        }
        .fixed-icon {
            color: #4caf50;
        }
        .not-fixed-icon {
            color: #9e9e9e;
        }
        .fixed-date {
            font-size: 11px;
            margin-left: 4px;
        }
        .status-violation {
            color: #f44336;
            font-weight: 500;
        }
        .status-fixed {
            color: #4caf50;
            font-weight: 500;
        }
        .status-ignored {
            color: #ff9800;
            font-weight: 500;
        }
        .inline-spinner {
            display: inline-block;
            margin-right: 6px;
        }
        :host ::ng-deep .mat-mdc-tab-labels {
            border-bottom: 2px solid #bdbdbd;
            background-color: #f5f5f5;
        }
        :host ::ng-deep .mat-mdc-tab {
            font-size: 15px;
            font-weight: 600;
            letter-spacing: 0.3px;
            min-width: 140px;
            padding: 0 24px;
            opacity: 0.7;
        }
        :host ::ng-deep .mat-mdc-tab:hover {
            background-color: #e0e0e0;
            opacity: 1;
        }
        :host ::ng-deep .mat-mdc-tab.mdc-tab--active {
            opacity: 1;
            background-color: #fff;
        }
        :host ::ng-deep .events-container button .mat-icon {
            vertical-align: middle;
            margin-right: 4px;
            font-size: 20px;
            height: 20px;
            width: 20px;
            line-height: 20px;
        }
        .busy-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: rgba(255, 255, 255, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
        }
    `]
})
export class CommissionRulesGridComponent implements OnInit {

    items: CommissionRuleItem[] = [];
    sort?: Partial<Sort>;
    page?: Partial<PageEvent & {totalItemCount?: number}>;
    isTogglePending = false;
    isEvaluating = false;
    evalTotalEvaluated = 0;
    evalTotalViolations = 0;
    evalTotalRemaining = 0;
    isFixing = false;
    isForceFixing = false;
    forceFixTotalFixed = 0;
    forceFixTotalSkipped = 0;
    forceFixTotalRemaining = 0;
    activeTab = 0;
    private readonly azureAuthService = inject(AzureAuthService);
    isDirector = this.azureAuthService.checkUserAzureTokenHasDirectorRole();

    evaluationItems: CommissionEvaluationItem[] = [];
    evaluationPage?: Partial<PageEvent & {totalItemCount?: number}>;
    evaluationStatusFilter = '';
    evaluationDateFrom = new Date(new Date().getFullYear(), new Date().getMonth() - 1, new Date().getDate());
    evaluationDateTo = new Date();

    displayedColumns = [
        'name',
        'criteria',
        'priority',
        'enabled',
        'paused',
        'autoFixDelay',
        'action',
    ];

    evaluationColumns = [
        'workflowName',
        'ruleName',
        'adxBookingId',
        'adxAgentBookingId',
        'status',
        'actual',
        'expected',
        'isFixed',
        'dateCreated',
        'evaluationAction',
    ];

    constructor(
        private modal: ModalService,
        private commissionRulesService: CommissionRulesService,
        private _snackBar: MatSnackBar,
    ) {}

    get isBusy(): boolean {
        return this.isEvaluating || this.isFixing || this.isForceFixing;
    }

    ngOnInit(): void {
        this.page = {
            pageSize: 25,
            pageIndex: 0,
            totalItemCount: 0,
        };

        this.getItems();
    }

    getItems() {
        this.commissionRulesService.getRules({
            page: this.page,
            sort: this.sort,
        }).subscribe((data) => {
            this.items = data?.items;
            this.page = {
                ...this.page,
                pageIndex: data.pagination.pageNumber - 1,
                totalItemCount: data.pagination.totalItemCount,
            };
        });
    }

    handleSortChange(sort: Sort) {
        this.sort = sort;

        if (!this.sort?.active || !this.sort?.direction) {
            this.sort = {};
        }

        this.getItems();
    }

    handlePageChanged(page: PageEvent) {
        this.page = {
            ...this.page,
            ...page,
        };

        this.getItems();
    }

    handleOpenRule(workflowId?: number) {
        const modalRef = this.modal.open(CommissionRuleModalComponent, {
            width: '40%',
            height: '100%',
            position: {right: '0'},
            panelClass: 'overflowAuto',
            data: workflowId ? `${workflowId}` : undefined,
        });
        modalRef.afterClosed().subscribe((completed) => {
            if (completed) {
                this.getItems();
            }
        });
    }

    handleToggleEnabled(item: CommissionRuleItem) {
        this.isTogglePending = true;
        this.commissionRulesService.getRuleDetails(`${item.workflowId}`).subscribe({
            next: (data) => {
                const request = {...data.workflow, isEnabled: item.isEnabled};
                this.commissionRulesService.updateRule(request).subscribe({
                    next: () => { this.isTogglePending = false; },
                    error: () => {
                        this.getItems();
                        this.isTogglePending = false;
                    },
                });
            },
            error: () => {
                item.isEnabled = !item.isEnabled;
                this.isTogglePending = false;
            }
        });
    }

    handleTogglePaused(item: CommissionRuleItem) {
        this.isTogglePending = true;
        this.commissionRulesService.getRuleDetails(`${item.workflowId}`).subscribe({
            next: (data) => {
                const request = {...data.workflow, isPaused: item.isPaused};
                this.commissionRulesService.updateRule(request).subscribe({
                    next: () => { this.isTogglePending = false; },
                    error: () => {
                        this.getItems();
                        this.isTogglePending = false;
                    },
                });
            },
            error: () => {
                item.isPaused = !item.isPaused;
                this.isTogglePending = false;
            }
        });
    }

    handleDeleteRule(item: CommissionRuleItem) {
        if (item.workflowId) {
            const modalRef = this.modal.open(CommissionRuleDeleteDialogComponent, {
                data: {workflowId: item.workflowId, ruleName: item.name},
            });

            modalRef.afterClosed().subscribe((deleted) => {
                if (deleted) {
                    this.getItems();
                }
            });
        }
    }

    evaluatingWorkflowId?: number;

    handleRunEvaluation(workflowId?: number) {
        this.isEvaluating = true;
        this.evaluatingWorkflowId = workflowId;
        this.evalTotalEvaluated = 0;
        this.evalTotalViolations = 0;
        this.evalTotalRemaining = 0;
        this.runEvaluationBatch();
    }

    private runEvaluationBatch() {
        this.commissionRulesService.runEvaluation(this.evaluatingWorkflowId).subscribe({
            next: (result) => {
                this.evalTotalEvaluated += result.totalEvaluated;
                this.evalTotalViolations += result.violationsFound;
                this.evalTotalRemaining = result.totalRemaining;

                this._snackBar.open(
                    `Batch done: ${result.totalEvaluated} checked, ${result.violationsFound} violations | Total: ${this.evalTotalEvaluated} checked | Remaining: ${result.totalRemaining}`,
                    'Dismiss',
                    {horizontalPosition: 'center', verticalPosition: 'bottom', duration: 5000}
                );

                if (result.hasMore) {
                    this.runEvaluationBatch();
                } else {
                    this.isEvaluating = false;
                    this.evaluatingWorkflowId = undefined;
                    this._snackBar.open(
                        `Evaluation complete: ${this.evalTotalEvaluated} checked, ${this.evalTotalViolations} violations found`,
                        'Dismiss',
                        {horizontalPosition: 'center', verticalPosition: 'bottom'}
                    );
                    if (this.activeTab === 1) {
                        this.getEvaluations();
                    }
                }
            },
            error: () => {
                this.isEvaluating = false;
                this.evaluatingWorkflowId = undefined;
                this._snackBar.open(
                    `Evaluation failed after ${this.evalTotalEvaluated} checked`,
                    'Dismiss',
                    {horizontalPosition: 'center', verticalPosition: 'bottom'}
                );
            }
        });
    }

    fixWorkflowId?: number;
    fixTotalFixed = 0;
    fixTotalSkipped = 0;
    fixTotalRemaining = 0;

    handleRunFixes(workflowId?: number) {
        this.isFixing = true;
        this.fixWorkflowId = workflowId;
        this.fixTotalFixed = 0;
        this.fixTotalSkipped = 0;
        this.fixTotalRemaining = 0;
        this.runFixBatch();
    }

    private runFixBatch() {
        this.commissionRulesService.runFixes(this.fixWorkflowId).subscribe({
            next: (result) => {
                this.fixTotalFixed += result.totalFixed;
                this.fixTotalSkipped += result.totalSkipped;
                this.fixTotalRemaining = result.totalRemaining;

                this._snackBar.open(
                    `Batch done: ${result.totalFixed} fixed, ${result.totalSkipped} skipped | Total: ${this.fixTotalFixed} fixed | Remaining: ${result.totalRemaining}`,
                    'Dismiss',
                    {horizontalPosition: 'center', verticalPosition: 'bottom', duration: 5000}
                );

                if (result.hasMore) {
                    this.runFixBatch();
                } else {
                    this.isFixing = false;
                    this.fixWorkflowId = undefined;
                    this._snackBar.open(
                        `Fixes complete: ${this.fixTotalFixed} fixed, ${this.fixTotalSkipped} skipped`,
                        'Dismiss',
                        {horizontalPosition: 'center', verticalPosition: 'bottom'}
                    );
                    if (this.activeTab === 1) {
                        this.getEvaluations();
                    }
                }
            },
            error: () => {
                this.isFixing = false;
                this.fixWorkflowId = undefined;
                this._snackBar.open(
                    `Fix run failed after ${this.fixTotalFixed} fixed`,
                    'Dismiss',
                    {horizontalPosition: 'center', verticalPosition: 'bottom'}
                );
            }
        });
    }

    forceFixWorkflowId?: number;

    handleForceApplyFixes(workflowId?: number) {
        this.isForceFixing = true;
        this.forceFixWorkflowId = workflowId;
        this.forceFixTotalFixed = 0;
        this.forceFixTotalSkipped = 0;
        this.forceFixTotalRemaining = 0;
        this.runForceFixBatch();
    }

    private runForceFixBatch() {
        this.commissionRulesService.forceApplyFixes(this.forceFixWorkflowId).subscribe({
            next: (result) => {
                this.forceFixTotalFixed += result.totalFixed;
                this.forceFixTotalSkipped += result.totalSkipped;
                this.forceFixTotalRemaining = result.totalRemaining;

                this._snackBar.open(
                    `Batch done: ${result.totalFixed} fixed, ${result.totalSkipped} skipped | Total: ${this.forceFixTotalFixed} fixed | Remaining: ${result.totalRemaining}`,
                    'Dismiss',
                    {horizontalPosition: 'center', verticalPosition: 'bottom', duration: 5000}
                );

                if (result.hasMore) {
                    this.runForceFixBatch();
                } else {
                    this.isForceFixing = false;
                    this.forceFixWorkflowId = undefined;
                    this._snackBar.open(
                        `Force apply complete: ${this.forceFixTotalFixed} fixed, ${this.forceFixTotalSkipped} skipped`,
                        'Dismiss',
                        {horizontalPosition: 'center', verticalPosition: 'bottom'}
                    );
                    if (this.activeTab === 1) {
                        this.getEvaluations();
                    }
                }
            },
            error: () => {
                this.isForceFixing = false;
                this.forceFixWorkflowId = undefined;
                this._snackBar.open(
                    `Force fix failed after ${this.forceFixTotalFixed} fixed`,
                    'Dismiss',
                    {horizontalPosition: 'center', verticalPosition: 'bottom'}
                );
            }
        });
    }

    handleTabChange(index: number) {
        this.activeTab = index;
        if (index === 1) {
            this.evaluationPage = {
                pageSize: 25,
                pageIndex: 0,
                totalItemCount: 0,
            };
            this.getEvaluations();
        }
    }

    getEvaluations() {
        const params: Record<string, string | number> = {
            pageSize: this.evaluationPage?.pageSize || 25,
            pageNumber: (this.evaluationPage?.pageIndex || 0) + 1,
            propertyName: 'dateCreated',
            orderKey: 'desc',
        };
        if (this.evaluationStatusFilter) {
            params['status'] = this.evaluationStatusFilter;
        }
        if (this.evaluationDateFrom) {
            params['dateFrom'] = this.evaluationDateFrom.toISOString();
        }
        if (this.evaluationDateTo) {
            params['dateTo'] = this.evaluationDateTo.toISOString();
        }
        this.commissionRulesService.getEvaluations(params).subscribe((data) => {
            this.evaluationItems = data?.items;
            this.evaluationPage = {
                ...this.evaluationPage,
                pageIndex: data.pagination.pageNumber - 1,
                totalItemCount: data.pagination.totalItemCount,
            };
        });
    }

    handleEvaluationPageChanged(page: PageEvent) {
        this.evaluationPage = {
            ...this.evaluationPage,
            ...page,
        };
        this.getEvaluations();
    }

    handleEvaluationDateChange() {
        this.evaluationPage = {
            ...this.evaluationPage,
            pageIndex: 0,
        };
        this.getEvaluations();
    }

    handleEvaluationStatusFilter(status: string) {
        this.evaluationStatusFilter = status;
        this.evaluationPage = {
            ...this.evaluationPage,
            pageIndex: 0,
        };
        this.getEvaluations();
    }

    formatExpectedValues(item: CommissionEvaluationItem): string {
        if (!item.expectedValues?.length) return '';
        return item.expectedValues
            .map(v => `${v.fieldName}: ${v.actualValue} → ${v.expectedValue}`)
            .join(', ');
    }

    handleUpdateEvaluationStatus(item: CommissionEvaluationItem, status: string) {
        let reason: string | undefined;

        if (status === 'Ignored') {
            const input = prompt('Reason for ignoring this evaluation:');
            if (input === null) return;
            reason = input;
        }

        this.commissionRulesService.updateEvaluationStatus(item.id, status, reason).subscribe({
            next: () => {
                this._snackBar.open(`Evaluation marked as ${status}`, 'Dismiss', {
                    horizontalPosition: 'center', verticalPosition: 'bottom'
                });
                this.getEvaluations();
            },
            error: () => {
                this._snackBar.open(`Failed to update evaluation status`, 'Dismiss', {
                    horizontalPosition: 'center', verticalPosition: 'bottom'
                });
            }
        });
    }
}
